/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-04-20 00:00:00
 *
 *******************************************************/
// @flow

import React, { Component } from 'react';
import { Modal, TextContainer } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

type Props = {
  active: boolean,
  handleChangeModal: Function
};

type ComposedProps = Props & WithI18nProps;

class TermsConditionsModal extends Component<ComposedProps> {
  state = {
    isScrollToBottom: false
  };

  render() {
    const { isScrollToBottom } = this.state;
    const { i18n, active, content } = this.props;

    return (
      <Modal
        open={active}
        limitHeight={true}
        onClose={() => this.handleChange()}
        title={i18n.translate('AdminHTML.Subscription.TermsConditions.title')}
        primaryAction={{
          disabled: !isScrollToBottom,
          content: i18n.translate(`AdminHTML.Common.Action.accept`),
          onAction: () => {
            this.handleSubscribe();
          }
        }}
        secondaryActions={[
          {
            disabled: !isScrollToBottom,
            content: i18n.translate('AdminHTML.Common.Action.decline'),
            onAction: this.handleChange
          }
        ]}
        onScrolledToBottom={this.handleScroll}>
        <Modal.Section>
          <div>
            <TextContainer>
              <div
                dangerouslySetInnerHTML={{
                  __html: content
                }}></div>
            </TextContainer>
          </div>
        </Modal.Section>
      </Modal>
    );
  }

  handleChange = () => {
    // this.setState({ isScrollToBottom: false });
    this.props.handleChangeModal();
  };

  handleScroll = () => {
    this.setState({ isScrollToBottom: true });
  };

  handleSubscribe = () => {
    // this.setState({ isScrollToBottom: false });
    this.props.handleChangeModal();
    this.props.subscribe();
  };
}

export default withI18n()(TermsConditionsModal);

// Note default Terms & Conditions template:

// const contentTest = () =>
//   `
//     <div style="color: #D72C0D;">Please read all the terms carefully.</div>
//     <div style="font-weight: 600; padding: 1.5rem 0 0.5rem 0;">1. Validity of coupon code</div>
//     <ul style="margin-block-start: 0; padding-inline-start: 2.1rem; margin-block-end: 0;list-style-type: lower-alpha">
//       <li style="padding-top: 0.5rem">
//         The coupon code is valid until
//         <span style="color: #D72C0D; font-weight: 600">{{ validDate }} (EST)</span> only. Boost
//         Commerce reserves the right to modify or cancel it at any time.
//       </li>
//       <li style="padding-top: 0.5rem">
//         Boost commerce will not be responsible for any issues in your billing that were issued after
//         the valid date of the coupon code.
//       </li>
//       <li style="padding-top: 0.5rem">
//         When you apply the coupon code for the plan you choose, you will get
//         <span style="color: #D72C0D; font-weight: 600">{{ discountAmount }}% off</span> for your
//         chosen Boost plan for
//         <span style="color: #D72C0D; font-weight: 600"> the next 3-month bill</span>. The offer of
//         coupon code <span style="color: #D72C0D; font-weight: 600">will not be applied</span> to the
//         new subscription plan of this store after you change this plan to another or cancel it and
//         then re-subscribe.
//       </li>
//       <li style="padding-top: 0.5rem">After 3 months, you will be <span style="font-weight: 600">automatically subscribed to the new plan</span> (without any discount), which is based on the product numbers and Shopify Plan of your store before you used the coupon code.</li>
//       <li style="padding-top: 0.5rem">The coupon code is only applicable to <span style="font-weight: 600">users who have submitted feature survey forms/agreed to do the feedback interview (about new features)</span> only.</li>
//       <li style="padding-top: 0.5rem">The coupon isn’t applicable for users who have joined Affiliate/ Agency Program.</li>
//       <li style="padding-top: 0.5rem">The coupon code is only applied <span style="color: #D72C0D; font-weight: 600">once</span> per store.</li>
//       <li style="padding-top: 0.5rem">The coupon code is valid for your current pricing plan and the new pricing model that we plan to release in May (est.) without premium add-ons.</li>
//     </ul>
//     <div style="font-weight: 600; padding: 1.5rem 0 0.5rem 0;">2. Coupon redemption</div>
//     <div style="padding-left: 1rem">
//       You must enter the coupon code in the provided link provided to you by Boost Commerce during checkout for the plan you choose. All the checkout issues that happen outside of the Product Filter & Search App will not be under the responsibility of Boost Commerce.
//     </div>
//     <div style="font-weight: 600; padding: 1.5rem 0 0.5rem 0;">
//       3. Combination with other offers
//     </div>
//     <ul style="margin-block-start: 0; padding-inline-start: 2.1rem; margin-block-end: 0;list-style-type: decimal;">
//       <li style="padding-top: 0.5rem">You cannot use a Discount Coupon in conjunction with any other sales discounts or promotional offers provided by Boost Commerce</li>
//       <li style="padding-top: 0.5rem">The coupon code will not be applied to our old pricing plans which were no longer available after July 1st, 2020.</li>
//     </ul>
//     <div style="font-weight: 600; padding: 1.5rem 0 0.5rem 0;">4. Refund policy</div>
//     <div style="padding-left: 1rem">No refund is applied for any billings that use the coupon code. If you are wrongly charged due to the errors of our system within the valid date of the coupon code, please contact our support team at support@boostcommerce.net for further help.</div>
//     <div style="font-weight: 600; padding: 1.5rem 0 0.5rem 0;">5. Related issues</div>
//     <ul style="margin-block-start: 0; padding-inline-start: 2.1rem; margin-block-end: 0;list-style-type: decimal;">
//       <li style="padding-top: 0.5rem">
//         If any issues arise when you apply the coupon code or within 30 days from the latest Shopify
//         billing date after using the discount code, you can contact our support team at
//         support@boostcommerce.net for further help. After 30 days since that Shopify billing date,
//         if you don't contact us, we suppose that you have no issues with the coupon code or our
//         system.
//         <span style="font-style: italic">

//           Eg: If you use the coupon code on 1/10/2022 and receive the Shopify billing on 20/10/2022,
//           if any issues caused by the coupon code or our system arise from 1/10 until 20/10, Boost
//           will take responsibility for them.
//         </span>
//       </li>
//       <li style="padding-top: 0.5rem">

//         We don't take any responsibility for any issues that were not caused by the coupon code or
//         our system.
//       </li>
//     </ul>
//   `;
